import { Fragment, useState } from 'react';
import {
  ComposedChart, BarChart, Bar, Line, XAxis, YAxis, Tooltip,
  ResponsiveContainer, CartesianGrid,
} from 'recharts';
import { COLORS, TICK, fmtK, axisFmt, FC_SERIES, YoYData, pctChange } from './format';
import { Empty } from './kpi';
import { ChartFrame } from './ChartFrame';
import { readableInk } from '@/shared/utils/contrast';
import { DeltaChip } from '@/components/ui/DeltaChip';

// Margins of the two compact charts (Monthly Revenue, P&L Monthly on Home and
// the Summary tab). The left margin was -8, which with a 38px axis pushed a
// five-character label ("-3.0M") past the SVG's left edge and cut its minus.
export const CHART_COMPACT_MARGIN = { top: 4, right: 4, left: 0, bottom: -4 };

export function MonthlyValueChart({ data, color, M, onBarClick, height = 300, title = 'Monthly', tools }: { data: Array<{ month: string; value: number; cumulative: number }>; color: string; M: (n: number) => string; onBarClick?: (monthIndex: number) => void; height?: number | string; title?: string; tools?: boolean }) {
  const series = [
    { key: 'value', name: 'Monthly', color },
    { key: 'cumulative', name: 'Cumulative', color: COLORS.cumulative, summary: false },
  ];
  return (
    <ChartFrame title={title} data={data} series={series} format={M} height={height} tools={tools}>
      {({ animate, hidden }) => (
        <ResponsiveContainer>
          <ComposedChart accessibilityLayer data={data} margin={{ top: 10, right: 12, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={COLORS.grid} vertical={false} />
            <XAxis dataKey="month" tick={{ fontSize: TICK, fill: COLORS.tick }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: TICK, fill: COLORS.tick }} width={52} tickFormatter={axisFmt} axisLine={false} tickLine={false} />
            <Tooltip formatter={(v) => M(Number(v))} />
            <Bar isAnimationActive={animate} hide={hidden('value')} dataKey="value" name="Monthly" fill={color} radius={[3, 3, 0, 0]} cursor={onBarClick ? 'pointer' : undefined} onClick={onBarClick ? (_d: unknown, index: number) => onBarClick(index) : undefined} />
            <Line isAnimationActive={animate} hide={hidden('cumulative')} type="monotone" dataKey="cumulative" name="Cumulative" stroke={COLORS.cumulative} strokeWidth={2} dot={false} />
          </ComposedChart>
        </ResponsiveContainer>
      )}
    </ChartFrame>
  );
}

// Monthly Revenue — current year vs prior year (grouped bars) + dashed trend
// line. `data` rows carry cy/py plus a `trend` value (null gaps allowed).
export function RevenueCyPyChart({ data, year, M, height = 188, title = 'Monthly Revenue', tools, variant = 'bars' }: {
  data: Array<{ month: string; cy: number; py: number; trend: number | null }>;
  year: number; M: (n: number) => string; height?: number | string; title?: string; tools?: boolean; variant?: 'bars' | 'lines';
}) {
  const series = [
    { key: 'py', name: String(year - 1), color: COLORS.py },
    { key: 'cy', name: String(year), color: variant === 'lines' ? COLORS.primary : COLORS.cy },
    { key: 'trend', name: 'Trend', color: COLORS.cumulative, summary: false, narrowHidden: true },
  ];
  return (
    <ChartFrame title={title} data={data} series={variant === 'lines' ? series.slice(0, 2) : series} format={M} height={height} tools={tools}>
      {({ animate, hidden, narrow }) => (
        <ResponsiveContainer>
          <ComposedChart accessibilityLayer data={data} margin={CHART_COMPACT_MARGIN} barCategoryGap="12%">
            <CartesianGrid strokeDasharray="3 3" stroke={COLORS.grid} vertical={false} />
            <XAxis dataKey="month" tick={{ fontSize: TICK, fill: COLORS.tick }} axisLine={false} tickLine={false} interval={narrow ? 'preserveStartEnd' : 0} />
            <YAxis tick={{ fontSize: TICK, fill: COLORS.tick }} width={44} tickFormatter={axisFmt} axisLine={false} tickLine={false} />
            <Tooltip formatter={(v) => M(Number(v))} />
            {variant === 'lines' ? (
              <>
                <Line isAnimationActive={animate} hide={hidden('py')} type="monotone" dataKey="py" name={String(year - 1)} stroke={COLORS.py} strokeWidth={2} strokeDasharray="5 4" dot={false} />
                <Line isAnimationActive={animate} hide={hidden('cy')} type="monotone" dataKey="cy" name={String(year)} stroke={COLORS.primary} strokeWidth={2.5} dot={false} />
              </>
            ) : (
              <>
                <Bar isAnimationActive={animate} hide={hidden('py')} dataKey="py" name={String(year - 1)} fill={COLORS.py} radius={[3, 3, 0, 0]} />
                <Bar isAnimationActive={animate} hide={hidden('cy')} dataKey="cy" name={String(year)} fill={COLORS.cy} radius={[3, 3, 0, 0]} />
              </>
            )}
            <Line isAnimationActive={animate} hide={hidden('trend') || narrow || variant === 'lines'} type="monotone" dataKey="trend" name="Trend" stroke={COLORS.cumulative} strokeWidth={2} strokeDasharray="6 4" dot={false} connectNulls />
          </ComposedChart>
        </ResponsiveContainer>
      )}
    </ChartFrame>
  );
}

// Profit & Loss — Monthly: income + expense bars with a net line overlay.
export function PnlMonthlyChart({ data, M, height = 188, title = 'Profit & Loss — Monthly', tools }: {
  data: Array<{ month: string; revenue: number; expense: number; net: number }>;
  M: (n: number) => string; height?: number | string; title?: string; tools?: boolean;
}) {
  const series = [
    { key: 'revenue', name: 'Income', color: COLORS.cy },
    { key: 'expense', name: 'Expenses', color: COLORS.expense },
    { key: 'net', name: 'Net', color: COLORS.net },
  ];
  return (
    <ChartFrame title={title} data={data} series={series} format={M} height={height} tools={tools}>
      {({ animate, hidden }) => (
        <ResponsiveContainer>
          <ComposedChart accessibilityLayer data={data} margin={CHART_COMPACT_MARGIN} barCategoryGap="12%">
            <CartesianGrid strokeDasharray="3 3" stroke={COLORS.grid} vertical={false} />
            <XAxis dataKey="month" tick={{ fontSize: TICK, fill: COLORS.tick }} axisLine={false} tickLine={false} interval={0} />
            <YAxis tick={{ fontSize: TICK, fill: COLORS.tick }} width={44} tickFormatter={axisFmt} axisLine={false} tickLine={false} />
            <Tooltip formatter={(v) => M(Number(v))} />
            <Bar isAnimationActive={animate} hide={hidden('revenue')} dataKey="revenue" name="Income" fill={COLORS.cy} radius={[3, 3, 0, 0]} />
            <Bar isAnimationActive={animate} hide={hidden('expense')} dataKey="expense" name="Expenses" fill={COLORS.expense} radius={[3, 3, 0, 0]} />
            <Line isAnimationActive={animate} hide={hidden('net')} type="monotone" dataKey="net" name="Net" stroke={COLORS.net} strokeWidth={2} dot={false} />
          </ComposedChart>
        </ResponsiveContainer>
      )}
    </ChartFrame>
  );
}
// A month without a figure is null: no bar, a blank table cell, never 0.
export function CompareBars({ data, year, M, barColor, title = 'This year vs last', height = 300, tools }: {
  data: Array<{ month: string; cy: number | null; py: number | null }>;
  year: number; M: (n: number) => string; barColor: string; title?: string; height?: number | string; tools?: boolean;
}) {
  const series = [
    { key: 'py', name: String(year - 1), color: COLORS.py },
    { key: 'cy', name: String(year), color: barColor },
  ];
  return (
    <ChartFrame title={title} data={data} series={series} format={M} height={height} tools={tools}>
      {({ animate, hidden }) => (
        <ResponsiveContainer>
          <BarChart accessibilityLayer data={data} margin={{ top: 10, right: 12, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={COLORS.grid} vertical={false} />
            <XAxis dataKey="month" tick={{ fontSize: TICK, fill: COLORS.tick }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: TICK, fill: COLORS.tick }} width={52} tickFormatter={axisFmt} axisLine={false} tickLine={false} />
            <Tooltip formatter={(v) => M(Number(v))} />
            <Bar isAnimationActive={animate} hide={hidden('py')} dataKey="py" name={String(year - 1)} fill={COLORS.py} radius={[3, 3, 0, 0]} />
            <Bar isAnimationActive={animate} hide={hidden('cy')} dataKey="cy" name={String(year)} fill={barColor} radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      )}
    </ChartFrame>
  );
}
export function YoyBars({ data, keyCy, keyPy, year, M, barColor, title = 'Revenue — this year vs last', tools }: { data: YoYData['rows']; keyCy: keyof YoYData['rows'][number]; keyPy: keyof YoYData['rows'][number]; year: number; M: (n: number) => string; barColor: string; title?: string; tools?: boolean }) {
  const figure = (v: unknown): number | null => (v == null || v === '' ? null : Number(v) || 0);
  const rows = data.map((r) => ({ month: r.month, cy: figure(r[keyCy]), py: figure(r[keyPy]) }));
  return <CompareBars data={rows} year={year} M={M} barColor={barColor} title={title} tools={tools} />;
}
// A bullet bar (charts-06): the actual amount against a comparison, with the
// real percentage (109% is 109%, never capped), the part above the target
// drawn past its tick, and no number at all when there is nothing to compare.
export function bulletPct(value: number, target: number): number | null {
  return target > 0 && Number.isFinite(value) ? Math.round((value / target) * 100) : null;
}

// Widths in % of the track: the actual bar up to the target, the overshoot
// past it, the target tick. Never negative, never above 100, always finite.
export function bulletGeometry(value: number, target: number): { actualW: number; overW: number; tick: number | null } {
  const shown = Math.max(0, Number.isFinite(value) ? value : 0);
  const goal = Number.isFinite(target) && target > 0 ? target : 0;
  const scale = Math.max(goal, shown) * 1.1 || 1;
  const upTo = goal > 0 ? Math.min(shown, goal) : shown;
  return {
    actualW: (upTo / scale) * 100,
    overW: goal > 0 && shown > goal ? ((shown - goal) / scale) * 100 : 0,
    tick: goal > 0 ? (goal / scale) * 100 : null,
  };
}

export function BulletBar({ value, target, fmt, label }: { value: number; target: number; fmt: (n: number) => string; label: string }) {
  const pct = bulletPct(value, target);
  const { actualW, overW, tick } = bulletGeometry(value, target);
  const amount = Number.isFinite(value) ? fmt(value) : '—';
  const name = pct == null ? `${label}: ${amount}, no comparison` : `${label}: ${pct}% of ${fmt(target)} (${amount})`;
  return (
    <div role="img" aria-label={name} className="flex flex-col gap-1.5 py-1">
      <div className="flex items-baseline justify-between gap-2" aria-hidden="true">
        <span className="text-figure text-primary-text tabular-nums">{amount}</span>
        <span className={`text-[0.75rem] font-bold tabular-nums ${pct != null && pct >= 100 ? 'text-success' : 'text-secondary'}`}>{pct == null ? '—' : `${pct}%`}</span>
      </div>
      <div className="relative h-3 rounded-full bg-border-light" aria-hidden="true">
        <div data-part="actual" className={`absolute inset-y-0 left-0 ${overW > 0 ? 'rounded-l-full' : 'rounded-full'}`} style={{ width: `${actualW}%`, background: COLORS.primary }} />
        {overW > 0 && <div data-part="overshoot" className="absolute inset-y-0 rounded-r-full" style={{ left: `${actualW}%`, width: `${overW}%`, background: COLORS.revenue }} />}
        {tick != null && <div data-part="target" className="absolute -inset-y-1 w-0.5 -translate-x-1/2 rounded bg-primary-text" style={{ left: `${tick}%` }} />}
      </div>
    </div>
  );
}
export function GaugeLegend({ cy, py, fmt, labels }: { cy: number; py: number; fmt: (n: number) => string; labels: string[] }) {
  return (
    <div className="flex items-center justify-center gap-4 text-[0.6875rem] -mt-1">
      <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ background: COLORS.primary }} /><span className="text-secondary">{labels[0]}</span><span className="font-semibold text-primary-text tabular-nums">{fmt(cy)}</span></span>
      <span className="flex items-center gap-1.5"><span data-swatch="target" className="w-0.5 h-3 rounded bg-primary-text" /><span className="text-secondary">{labels[1]}</span><span className="font-semibold text-primary-text tabular-nums">{fmt(py)}</span></span>
    </div>
  );
}
export function DeltaBadge({ value, note }: { value: number | null; note: string }) {
  if (value == null) return <span className="text-label uppercase tracking-[0.12em] text-muted">{note}</span>;
  return (
    <div className="text-right leading-tight">
      <DeltaChip value={value} context={note} />
      <div aria-hidden="true" className="text-label uppercase tracking-[0.12em] text-muted">{note}</div>
    </div>
  );
}
export function DeliveryBar({ received, pending }: { received: number; pending: number }) {
  const total = received + pending;
  const recPct = total > 0 ? (received / total) * 100 : 0;
  return (
    <div className="space-y-3 pt-2">
      <div className="h-3 w-full rounded-full bg-border-light overflow-hidden flex">
        <div className="h-full bg-emerald-500" style={{ width: `${recPct}%` }} />
        <div className="h-full bg-orange-400" style={{ width: `${100 - recPct}%` }} />
      </div>
      <div className="flex items-center justify-between text-body"><span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-emerald-500" />Received</span><span className="font-numeric tabular-nums font-semibold">{fmtK(received)}</span></div>
      <div className="flex items-center justify-between text-body"><span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-orange-400" />Outstanding</span><span className="font-numeric tabular-nums font-semibold">{fmtK(pending)}</span></div>
      {total === 0 && <Empty />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// DonutShare — donut chart + legend list. Center label shows a count + caption.
// Faithful to PieChartComponent.jsx (outerR = size/2-4, innerR = outerR*0.54,
// 2px white stroke, hover pop, center count). Legend rows: name + value + %.
// ---------------------------------------------------------------------------
export type DonutItem = { label: string; value: number; pct: number; color: string };
export function DonutShare({ items, size = 170, centerValue, centerCaption, fmtValue, onItemClick }: {
  items: DonutItem[];
  size?: number;
  centerValue?: React.ReactNode;   // defaults to items.length
  centerCaption?: string;          // defaults to "items"
  fmtValue?: (n: number) => string;
  onItemClick?: (item: DonutItem) => void;  // makes each legend row clickable (drill-down)
}) {
  const [hovered, setHovered] = useState<number | null>(null);
  const total = items.reduce((s, x) => s + Math.abs(x.value), 0);
  if (!items.length || !total) return <Empty />;
  const cx = size / 2, cy = size / 2;
  const outerR = size / 2 - 4;
  const innerR = outerR * 0.54;
  const fmtV = fmtValue ?? ((n: number) => fmtK(n));

  let angle = -Math.PI / 2;
  const paths = items.map((sl, i) => {
    const frac = Math.abs(sl.value) / total;
    const a1 = angle; angle += frac * 2 * Math.PI;
    const a2 = angle;
    const rr = hovered === i ? outerR + 4 : outerR;
    const x1o = cx + rr * Math.cos(a1), y1o = cy + rr * Math.sin(a1);
    const x2o = cx + rr * Math.cos(a2), y2o = cy + rr * Math.sin(a2);
    const x1i = cx + innerR * Math.cos(a2), y1i = cy + innerR * Math.sin(a2);
    const x2i = cx + innerR * Math.cos(a1), y2i = cy + innerR * Math.sin(a1);
    const large = frac > 0.5 ? 1 : 0;
    const d = `M${x1o},${y1o}A${rr},${rr} 0 ${large},1 ${x2o},${y2o}L${x1i},${y1i}A${innerR},${innerR} 0 ${large},0 ${x2i},${y2i}Z`;
    return { d, color: sl.color, frac, name: sl.label, i };
  });

  return (
    <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img"
        aria-label={`${centerCaption ?? 'items'}: ${items.map((it) => `${it.label} ${it.pct.toFixed(0)}%`).join(', ')}`} style={{ flexShrink: 0, filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.10))' }}>
        {paths.map((p) => (
          <path key={p.i} d={p.d} fill={p.color} stroke="#fff" strokeWidth={2}
            style={{ transition: 'all 0.15s', cursor: onItemClick ? 'pointer' : 'default' }}
            onClick={onItemClick ? () => onItemClick(items[p.i]) : undefined}
            onMouseEnter={() => setHovered(p.i)} onMouseLeave={() => setHovered(null)}>
            <title>{`${p.name}: ${fmtV(items[p.i].value)} (${(p.frac * 100).toFixed(0)}%)`}</title>
          </path>
        ))}
        <circle cx={cx} cy={cy} r={innerR - 1} fill="#fff" />
        {hovered !== null ? (
          <>
            <text x={cx} y={cy - 6} textAnchor="middle" fontSize={size * 0.08} fontWeight="700" style={{ fill: 'var(--primary-text)' }}>{(paths[hovered].frac * 100).toFixed(0)}%</text>
            <text x={cx} y={cy + 10} textAnchor="middle" fontSize={size * 0.065} style={{ fill: 'var(--text-secondary)' }}>{(paths[hovered].name ?? '').length > 14 ? `${paths[hovered].name.slice(0, 13)}…` : paths[hovered].name}</text>
          </>
        ) : (
          <>
            <text x={cx} y={cy - 4} textAnchor="middle" fontSize={size * 0.11} fontWeight="700" style={{ fill: 'var(--primary-text)' }}>{centerValue ?? items.length}</text>
            <text x={cx} y={cy + 11} textAnchor="middle" fontSize={size * 0.068} style={{ fill: 'var(--text-muted)' }}>{centerCaption ?? 'items'}</text>
          </>
        )}
      </svg>
      <div style={{ flex: 1, minWidth: 0 }}>
        {items.map((it, i) => {
          const rowStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 3, marginBottom: 3, fontSize: '0.82em', padding: '3px 4px', borderRadius: 4, transition: 'background 0.15s', cursor: onItemClick ? 'pointer' : 'default' };
          const title = `${it.label}: ${fmtV(it.value)} (${it.pct.toFixed(1)}%)`;
          const kids = (
            <>
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: it.color, flexShrink: 0 }} />
              <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--text,#0f172a)' }}>{it.label}</span>
              <span style={{ fontWeight: 600, color: it.color, whiteSpace: 'nowrap', marginLeft: 2 }}>{fmtV(it.value)}</span>
              <span style={{ color: 'var(--text-muted,#94a3b8)', minWidth: 28, marginLeft: 2, fontSize: '0.9em', textAlign: 'right' }}>{it.pct.toFixed(0)}%</span>
            </>
          );
          const enter = (e: React.MouseEvent<HTMLElement>) => { e.currentTarget.style.background = 'var(--bg-input,#eff6ff)'; setHovered(i); };
          const leave = (e: React.MouseEvent<HTMLElement>) => { e.currentTarget.style.background = 'transparent'; setHovered(null); };
          return onItemClick ? (
            <button key={i} type="button" className="focus-ring w-full text-left" style={rowStyle}
              onMouseEnter={enter} onMouseLeave={leave}
              onFocus={() => setHovered(i)} onBlur={() => setHovered(null)}
              onClick={() => onItemClick(it)} title={title}>{kids}</button>
          ) : (
            <div key={i} style={rowStyle} onMouseEnter={enter} onMouseLeave={leave} title={title}>{kids}</div>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// OrderPipeline — horizontal split (stacked) bar + per-status detail rows and
// a total footer. Faithful to CustomerOrdersTab.jsx "Order Pipeline".
// Each status: { label, value, pct } — pct is the count share (drives bar
// width + first metric); valuePct (amount share) optional for the right column.
// ---------------------------------------------------------------------------
export type PipelineStatus = { label: string; value: number; pct: number; count?: number; valuePct?: number; color?: string };
export function OrderPipeline({ statuses, total, fmtValue, fmtNum }: {
  statuses: PipelineStatus[];
  total?: number;                       // total amount for the footer (defaults to sum of value)
  fmtValue?: (n: number) => string;
  fmtNum?: (n: number) => string;
}) {
  if (!statuses.length) return <Empty />;
  const fmtV = fmtValue ?? ((n: number) => fmtK(n));
  const fmtN = fmtNum ?? ((n: number) => String(Math.round(n)));
  const totalCount = statuses.reduce((a, b) => a + (b.count ?? 0), 0);
  const totalAmt = total ?? statuses.reduce((a, b) => a + b.value, 0);
  const colorFor = (st: PipelineStatus, i: number) => st.color ?? FC_SERIES[i % FC_SERIES.length];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* Stacked bar */}
      <div role="img" aria-label={statuses.filter((s) => s.pct > 0).map((s) => `${s.label} ${Math.round(s.pct)}%`).join(', ')}
        style={{ display: 'flex', borderRadius: 10, overflow: 'hidden', height: 36 }}>
        {statuses.map((st, i) => st.pct > 0 ? (
          <div key={i} style={{ width: st.pct + '%', background: colorFor(st, i), display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'width 0.3s' }}
            title={`${st.label}: ${fmtV(st.value)}`}>
            {st.pct > 8 && (() => { const ink = readableInk(colorFor(st, i)); return ink.ratio >= 4.5
              ? <span aria-hidden="true" style={{ fontSize: TICK, color: ink.ink, fontWeight: 700 }}>{Math.round(st.pct)}%</span>
              : null; })()}
          </div>
        ) : null)}
      </div>
      {/* Status detail rows */}
      {statuses.map((st, i) => {
        const amtPct = st.valuePct ?? (totalAmt > 0 ? (st.value / totalAmt) * 100 : 0);
        return (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: 'var(--bg-input,#f8fafc)', borderRadius: 10, border: '1px solid var(--border,#e8ecf0)' }}>
            <div style={{ width: 10, height: 28, borderRadius: 4, background: colorFor(st, i), flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text,#0f172a)' }}>{st.label}</div>
              <div style={{ fontSize: 11, color: 'var(--text-muted,#94a3b8)' }}>{fmtN(st.count ?? 0)} orders · {st.pct.toFixed(0)}%</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: colorFor(st, i) }}>{fmtV(st.value)}</div>
              <div style={{ fontSize: 10, color: 'var(--text-muted,#94a3b8)' }}>{amtPct.toFixed(0)}% of total</div>
            </div>
          </div>
        );
      })}
      {/* Total */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 12px', borderTop: '2px solid var(--border,#e2e8f0)' }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text,#0f172a)' }}>Total</span>
        <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--primary,#2563eb)' }}>{fmtV(totalAmt)} · {fmtN(totalCount)} orders</span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SeasonalityGrid — 12-month grid, two stacked colored cells per month
// (this year / last year) with a YoY delta beneath. Faithful to the
// "Seasonality — Year-over-Year" block in TrendCharts.jsx.
// ---------------------------------------------------------------------------
export type SeasonMonth = { label: string; value: number; prevValue: number };
export function SeasonalityGrid({ months, year, prevYear, fmtShort }: {
  months: SeasonMonth[];
  year: number | string;
  prevYear: number | string;
  fmtShort?: (n: number) => string;
}) {
  const fS = fmtShort ?? ((v: number) => (v ? fmtK(v) : ''));
  const maxRev = Math.max(...months.map((x) => x.value), 1);
  const maxPrev = Math.max(...months.map((x) => x.prevValue), 1);
  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.75em', color: '#7090b0' }}><span style={{ width: 10, height: 10, borderRadius: 2, background: '#0066cc', display: 'inline-block' }} />{year}</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.75em', color: '#7090b0' }}><span style={{ width: 10, height: 10, borderRadius: 2, background: '#a0b0c0', display: 'inline-block' }} />{prevYear}</span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(12,1fr)', gap: 6 }}>
        {months.map((m, i) => {
          const intensity = m.value > 0 ? Math.min(1, m.value / maxRev) : 0;
          const prevIntensity = m.prevValue > 0 ? Math.min(1, m.prevValue / maxPrev) : 0;
          const lyChg = m.value > 0 ? pctChange(m.value, m.prevValue) : null;
          return (
            <div key={i} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '0.78em', color: '#4a6080', marginBottom: 5, fontWeight: 700 }}>{m.label}</div>
              <div style={{ height: 48, borderRadius: 8, background: `rgba(0,102,204,${0.08 + intensity * 0.92})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.78em', color: intensity > 0.4 ? '#fff' : '#4a6080', fontWeight: 700, marginBottom: 4, transition: 'all 0.2s' }}>
                {fS(m.value) || '—'}
              </div>
              <div style={{ height: 36, borderRadius: 6, background: `rgba(140,155,175,${0.08 + prevIntensity * 0.72})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.72em', color: prevIntensity > 0.5 ? '#fff' : '#4a6080', fontWeight: 600, transition: 'all 0.2s' }}>
                {fS(m.prevValue) || '—'}
              </div>
              {lyChg != null && (
                <div style={{ marginTop: 5, display: 'flex', justifyContent: 'center' }}>
                  <DeltaChip value={lyChg} context={`vs ${prevYear}`} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// PnlStatement — the expandable P&L statement table. INCOME / OPERATING COSTS
// sections (headings) → items → account rows, with ACCT / period / TOTAL
// columns, an expand/collapse-all toolbar, gross-profit / before-tax / net
// summary rows. Faithful to PnlTab.jsx (3-level hierarchy + sticky desc col).
// ---------------------------------------------------------------------------
export type PnlAccount = { accountNo?: string; label: string; values: number[]; total: number; link?: string };
export type PnlItem = { name: string; accounts: PnlAccount[]; total: number; monthlyValues: number[] };
export type PnlHeading = { heading: string; kind?: 'income' | 'cogs' | 'expense'; items: PnlItem[]; total: number; monthlyValues: number[] };
export type PnlStatementProps = {
  headings: PnlHeading[];
  visibleMonths: number[];           // month indices (0–11) shown as columns
  monthNames?: string[];
  curSymbol?: string;
  title?: string;
  year?: number | string;
};
export function PnlStatement({ headings, visibleMonths, monthNames, curSymbol = '$', title = 'Profit & Loss', year }: PnlStatementProps) {
  const MN = monthNames ?? ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const [openHeadings, setOpenHeadings] = useState<Record<string, boolean>>({});
  const [openItems, setOpenItems] = useState<Record<string, boolean>>({});

  const fmtV = (v: number) => (v ? ((v < 0 ? '-' : '') + curSymbol + ' ' + Math.abs(v).toLocaleString('en-US', { maximumFractionDigits: 0 })) : '');
  const fmtN = (v: number) => (v ? v.toLocaleString('en-US', { maximumFractionDigits: 0 }) : '');

  // Section classification + summary figures.
  const isCogs = (h: PnlHeading) => h.kind === 'cogs' || (h.kind == null && /COST|COGS/i.test(h.heading));
  const isIncome = (h: PnlHeading) => h.kind === 'income' || (h.kind == null && /INCOME|REVENUE/i.test(h.heading));
  const incomeTotal = headings.filter(isIncome).reduce((s, h) => s + h.total, 0);
  const cogsTotal = headings.filter(isCogs).reduce((s, h) => s + h.total, 0);
  const expenseHeadings = headings.filter((h, i) => !isIncome(h) && !isCogs(h) && (h.kind === 'expense' || i > 0));
  const operatingTotal = expenseHeadings.reduce((s, h) => s + h.total, 0);
  const hasCogs = headings.some(isCogs);
  const grossProfit = incomeTotal - cogsTotal;
  const profitBeforeTax = incomeTotal - cogsTotal - operatingTotal;
  const grossMargin = incomeTotal > 0 ? (grossProfit / incomeTotal * 100) : 0;
  const pbtMargin = incomeTotal > 0 ? (profitBeforeTax / incomeTotal * 100) : 0;
  const netMargin = pbtMargin;
  const monthSum = (rows: { monthlyValues: number[] }[], m: number) => rows.reduce((s, r) => s + (r.monthlyValues[m] || 0), 0);
  const incomeMo = (m: number) => monthSum(headings.filter(isIncome), m);
  const cogsMo = (m: number) => monthSum(headings.filter(isCogs), m);
  const opMo = (m: number) => monthSum(expenseHeadings, m);

  const allOpen = headings.length > 0 && headings.every((h) => openHeadings[h.heading]);
  const toggleAll = () => {
    if (allOpen) { setOpenHeadings({}); setOpenItems({}); }
    else { const ho: Record<string, boolean> = {}; headings.forEach((h) => { ho[h.heading] = true; }); setOpenHeadings(ho); }
  };

  const pctBadge = (v: number): React.CSSProperties => ({
    display: 'inline-block', padding: '1px 6px', borderRadius: 8, fontSize: 10, fontWeight: 600,
    background: v > 0 ? '#dcfce7' : v < 0 ? '#fef2f2' : '#f1f5f9',
    color: v > 0 ? '#166534' : v < 0 ? '#991b1b' : '#64748b',
  });

  const nameCell: React.CSSProperties = { padding: '10px 12px', fontWeight: 700, fontSize: 13, position: 'sticky', left: 0, zIndex: 1, whiteSpace: 'nowrap' };
  const numCell: React.CSSProperties = { textAlign: 'right', padding: '10px 8px', fontVariantNumeric: 'tabular-nums', fontSize: 12 };
  const headerStyle: React.CSSProperties = { padding: '8px 8px', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em', color: '#64748b' };

  return (
    <div style={{ border: '1px solid var(--border,#e2e8f0)', borderRadius: 16, overflow: 'hidden', background: 'var(--surface,#fff)' }}>
      {/* Toolbar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid var(--border,#e2e8f0)' }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text,#0f172a)' }}>
          {title}{year != null ? <> — {year}</> : null}
          {visibleMonths.length > 0 ? <span style={{ fontWeight: 400, color: 'var(--text-muted,#94a3b8)', marginLeft: 6, fontSize: 12 }}>({visibleMonths.map((m) => MN[m]).join(', ')})</span> : null}
        </div>
        <button onClick={toggleAll} style={{ border: '1px solid var(--border,#e2e8f0)', background: 'var(--bg-card,#fff)', borderRadius: 8, padding: '5px 12px', fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', color: 'var(--text-muted,#94a3b8)', transition: 'all 0.15s' }}>
          {allOpen ? 'Collapse All' : 'Expand All'}
        </button>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#f8fafc', borderBottom: '2px solid var(--border,#e2e8f0)' }}>
              <th style={{ ...headerStyle, textAlign: 'left', position: 'sticky', left: 0, background: '#f8fafc', zIndex: 2, minWidth: 160 }}>Description</th>
              <th style={{ ...headerStyle, textAlign: 'left', background: '#f8fafc', zIndex: 2, minWidth: 50, width: 60 }}>Acct</th>
              {visibleMonths.map((m) => <th key={m} style={{ ...headerStyle, textAlign: 'right', minWidth: 80 }}>{MN[m]}</th>)}
              <th style={{ ...headerStyle, textAlign: 'right', padding: '8px 16px', minWidth: 100 }}>Total</th>
            </tr>
          </thead>
          <tbody>
            {headings.map((hd) => {
              const hOpen = !!openHeadings[hd.heading];
              return (
                <Fragment key={hd.heading}>
                  {/* Level 1: heading */}
                  <tr onClick={() => setOpenHeadings((p) => ({ ...p, [hd.heading]: !p[hd.heading] }))} style={{ background: '#e8ecf4', borderBottom: '2px solid var(--border,#e2e8f0)', cursor: 'pointer' }}>
                    <td colSpan={2} style={{ ...nameCell, background: '#e8ecf4', color: '#1e40af', fontSize: 13 }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                        <FcChevron open={hOpen} color="#1e40af" />
                        {hd.heading}
                      </span>
                    </td>
                    {!hOpen && visibleMonths.map((m) => <td key={m} style={{ ...numCell, fontWeight: 600, background: '#e8ecf4', color: (hd.monthlyValues[m] || 0) < 0 ? '#dc2626' : '#334155' }}>{fmtN(hd.monthlyValues[m])}</td>)}
                    {hOpen && visibleMonths.map((m) => <td key={m} style={{ background: '#e8ecf4' }} />)}
                    <td style={{ ...numCell, fontWeight: 700, fontSize: 13, background: '#e8ecf4', color: hd.total < 0 ? '#dc2626' : '#1e40af' }}>{!hOpen ? fmtV(hd.total) : ''}</td>
                  </tr>

                  {hOpen && hd.items.map((item) => {
                    const iKey = hd.heading + '/' + item.name;
                    const iOpen = !!openItems[iKey];
                    return (
                      <Fragment key={iKey}>
                        {/* Level 2: item */}
                        <tr onClick={() => setOpenItems((p) => ({ ...p, [iKey]: !p[iKey] }))} style={{ background: '#f8fafc', borderBottom: '1px solid var(--border,#e2e8f0)', cursor: 'pointer' }}>
                          <td style={{ ...nameCell, paddingLeft: 20, fontSize: 12, fontWeight: 600, color: '#475569', background: '#f8fafc' }}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                              <FcChevron open={iOpen} color="#94a3b8" />
                              {item.name}
                              <span style={{ fontSize: 10, color: '#94a3b8', fontWeight: 400 }}>({item.accounts.length})</span>
                            </span>
                          </td>
                          <td style={{ background: '#f8fafc' }} />
                          {visibleMonths.map((m) => <td key={m} style={{ ...numCell, fontSize: 11, background: '#f8fafc', color: (item.monthlyValues[m] || 0) < 0 ? '#dc2626' : '#64748b' }}>{fmtN(item.monthlyValues[m])}</td>)}
                          <td style={{ ...numCell, fontWeight: 600, background: '#f8fafc', color: item.total < 0 ? '#dc2626' : '#334155' }}>{fmtV(item.total)}</td>
                        </tr>

                        {/* Level 3: account rows */}
                        {iOpen && item.accounts.map((acct, ai) => (
                          <tr key={iKey + '/' + (acct.accountNo || '') + ai} style={{ background: '#fff', borderBottom: '1px solid #f1f5f9' }}>
                            <td style={{ ...nameCell, paddingLeft: 36, fontSize: 11, fontWeight: 400, color: '#64748b', background: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 280 }}>{acct.label}</td>
                            <td style={{ ...nameCell, padding: '8px 8px', fontSize: 11, fontWeight: 500, color: '#0066cc', background: '#fff' }}>
                              {acct.link ? <a href={acct.link} target="_blank" rel="noopener noreferrer" style={{ color: '#0066cc', textDecoration: 'underline dotted', cursor: 'pointer' }}>{acct.accountNo}</a> : (acct.accountNo || '')}
                            </td>
                            {visibleMonths.map((m) => <td key={m} style={{ ...numCell, fontSize: 11, color: (acct.values[m] || 0) < 0 ? '#dc2626' : '#94a3b8' }}>{fmtN(acct.values[m])}</td>)}
                            <td style={{ ...numCell, fontSize: 11, fontWeight: 500, color: acct.total < 0 ? '#dc2626' : '#475569' }}>{fmtV(acct.total)}</td>
                          </tr>
                        ))}
                        {/* Item subtotal (when expanded + multiple accounts) */}
                        {iOpen && item.accounts.length > 1 && (
                          <tr style={{ background: '#f1f5f9', borderTop: '1px solid #e2e8f0', borderBottom: '1px solid #e2e8f0' }}>
                            <td colSpan={2} style={{ ...nameCell, paddingLeft: 20, fontSize: 11, fontWeight: 700, color: '#334155', background: '#f1f5f9' }}>Total {item.name}</td>
                            {visibleMonths.map((m) => <td key={m} style={{ ...numCell, fontSize: 11, fontWeight: 600, background: '#f1f5f9', color: (item.monthlyValues[m] || 0) < 0 ? '#dc2626' : '#334155' }}>{fmtN(item.monthlyValues[m])}</td>)}
                            <td style={{ ...numCell, fontWeight: 700, fontSize: 12, background: '#f1f5f9', color: item.total < 0 ? '#dc2626' : '#0f172a' }}>{fmtV(item.total)}</td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}

                  {/* Heading subtotal (when expanded) */}
                  {hOpen && (
                    <tr style={{ background: '#f8fafc', borderTop: '2px solid var(--border,#e2e8f0)', borderBottom: '2px solid var(--border,#e2e8f0)' }}>
                      <td colSpan={2} style={{ ...nameCell, background: '#f8fafc', color: '#334155', fontSize: 12 }}>TOTAL {hd.heading}</td>
                      {visibleMonths.map((m) => <td key={m} style={{ ...numCell, fontWeight: 700, background: '#f8fafc', color: (hd.monthlyValues[m] || 0) < 0 ? '#dc2626' : '#334155' }}>{fmtN(hd.monthlyValues[m])}</td>)}
                      <td style={{ ...numCell, fontWeight: 700, fontSize: 13, background: '#f8fafc', color: hd.total < 0 ? '#dc2626' : '#0f172a' }}>{fmtV(hd.total)}</td>
                    </tr>
                  )}

                  {/* Gross profit after COGS */}
                  {isCogs(hd) && hasCogs && (
                    <tr style={{ background: '#ecfdf5', borderBottom: '2px solid #a7f3d0' }}>
                      <td colSpan={2} style={{ ...nameCell, background: '#ecfdf5', color: grossProfit >= 0 ? '#065f46' : '#991b1b', fontSize: 13 }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>Gross Profit<span style={pctBadge(grossMargin)}>{grossMargin >= 0 ? '+' : ''}{grossMargin.toFixed(1)}%</span></span>
                      </td>
                      {visibleMonths.map((m) => { const v = incomeMo(m) - cogsMo(m); return <td key={m} style={{ ...numCell, fontWeight: 700, color: v < 0 ? '#991b1b' : '#065f46', background: '#ecfdf5' }}>{fmtN(v)}</td>; })}
                      <td style={{ ...numCell, fontWeight: 700, fontSize: 14, color: grossProfit >= 0 ? '#065f46' : '#991b1b', background: '#ecfdf5' }}>{fmtV(grossProfit)}</td>
                    </tr>
                  )}

                  {/* Profit before tax after last expense heading */}
                  {expenseHeadings.length > 0 && hd.heading === expenseHeadings[expenseHeadings.length - 1].heading && (
                    <tr style={{ background: '#ecfdf5', borderBottom: '2px solid #a7f3d0' }}>
                      <td colSpan={2} style={{ ...nameCell, background: '#ecfdf5', color: profitBeforeTax >= 0 ? '#065f46' : '#991b1b', fontSize: 13 }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>Profit Before Tax<span style={pctBadge(pbtMargin)}>{pbtMargin >= 0 ? '+' : ''}{pbtMargin.toFixed(1)}%</span></span>
                      </td>
                      {visibleMonths.map((m) => { const v = incomeMo(m) - cogsMo(m) - opMo(m); return <td key={m} style={{ ...numCell, fontWeight: 700, color: v < 0 ? '#991b1b' : '#065f46', background: '#ecfdf5' }}>{fmtN(v)}</td>; })}
                      <td style={{ ...numCell, fontWeight: 700, fontSize: 14, color: profitBeforeTax >= 0 ? '#065f46' : '#991b1b', background: '#ecfdf5' }}>{fmtV(profitBeforeTax)}</td>
                    </tr>
                  )}
                </Fragment>
              );
            })}

            {/* Final net total */}
            <tr style={{ background: '#eff6ff', borderBottom: '2px solid #93c5fd' }}>
              <td colSpan={2} style={{ ...nameCell, background: '#eff6ff', color: '#1e40af', fontSize: 14 }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>TOTAL<span style={pctBadge(netMargin)}>{netMargin >= 0 ? '+' : ''}{netMargin.toFixed(1)}%</span></span>
              </td>
              {visibleMonths.map((m) => { const v = incomeMo(m) - cogsMo(m) - opMo(m); return <td key={m} style={{ ...numCell, fontWeight: 700, fontSize: 13, color: v < 0 ? '#991b1b' : '#1e40af', background: '#eff6ff' }}>{fmtN(v)}</td>; })}
              <td style={{ ...numCell, fontWeight: 800, fontSize: 15, color: profitBeforeTax >= 0 ? '#1e40af' : '#991b1b', background: '#eff6ff' }}>{fmtV(profitBeforeTax)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Chevron used by the P&L statement rows (rotates -90deg when collapsed).
function FcChevron({ open, color = '#94a3b8' }: { open: boolean; color?: string }) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      style={{ transition: 'transform 0.2s', transform: open ? 'rotate(0deg)' : 'rotate(-90deg)', flexShrink: 0 }}>
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}
